/**
 * Team Model
 * 
 * Represents a team participating in the tournament
 */
export default class Team {
  constructor({
    id = Date.now().toString(36) + Math.random().toString(36).substr(2, 5),
    name,
    color = '#3b82f6',
    logo = null,
    players = [],
    created = new Date(),
    lastUpdated = new Date()
  } = {}) {
    this.id = id;
    
    // Use color name as default team name if not specified
    const colorNames = {
      '#FF4136': 'Red',
      '#0074D9': 'Blue',
      '#2ECC40': 'Green',
      '#FFDC00': 'Yellow',
      '#FF851B': 'Orange',
      '#B10DC9': 'Purple',
      '#F012BE': 'Pink',
      '#7FDBFF': 'Cyan',
      '#001f3f': 'Navy',
      '#39CCCC': 'Teal',
      '#3b82f6': 'Blue'
    };
    
    this.name = name || `${colorNames[color] || 'Team'} Team`;
    this.color = color;
    this.logo = logo;
    this.players = players;
    this.created = created;
    this.lastUpdated = lastUpdated;
  }
  
  /**
   * Create a new Team instance from JSON data
   * @param {Object} data - JSON data
   * @returns {Team} New Team instance
   */
  static fromJSON(data) {
    return new Team({
      id: data.id,
      name: data.name,
      color: data.color,
      logo: data.logo,
      players: data.players ? [...data.players] : [],
      created: new Date(data.created),
      lastUpdated: new Date(data.lastUpdated)
    });
  }
  
  /**
   * Convert to JSON
   * @returns {Object} JSON representation
   */
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      color: this.color,
      logo: this.logo,
      players: [...this.players],
      created: this.created.toISOString(),
      lastUpdated: this.lastUpdated.toISOString()
    };
  }
  
  /**
   * Add a player to the team
   * @param {Object} player - Player object
   */
  addPlayer(player) {
    this.players.push(player);
    this.lastUpdated = new Date();
  }
  
  /**
   * Remove a player from the team
   * @param {string} playerId - Player ID to remove
   */
  removePlayer(playerId) {
    this.players = this.players.filter(p => p.id !== playerId);
    this.lastUpdated = new Date();
  }
  
  /**
   * Update a player's data
   * @param {string} playerId - Player ID to update
   * @param {Object} updates - Properties to update
   */
  updatePlayer(playerId, updates) {
    const playerIndex = this.players.findIndex(p => p.id === playerId);
    if (playerIndex !== -1) {
      this.players[playerIndex] = { ...this.players[playerIndex], ...updates };
      this.lastUpdated = new Date();
    }
  }
  
  /**
   * Update team properties
   * @param {Object} updates - Properties to update
   */
  update(updates) {
    Object.keys(updates).forEach(key => {
      if (key !== 'players' && key !== 'id') {
        this[key] = updates[key];
      }
    });
    
    if (updates.players) {
      this.players = [...updates.players];
    }
    
    this.lastUpdated = new Date();
  }
}
